import { Handle, Position } from '@xyflow/react';
import { Box, createSvgIcon } from '@mui/material';
import { IColumn } from '../../redux/_Dataset/types';
import { useRef, useState } from 'react';
import { AccessTime, CalendarToday } from '@mui/icons-material';
import { fontSize } from '../..';
import { TAlert } from '../../types/types';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import PhotoIcon from '@mui/icons-material/Photo';

interface ICanvasColumn {
  idx: number;
  column: IColumn;
  schema: string;
  database?: string;
  fromCustomQueryTable: boolean;
  tableId: string;
  tablename: string;
  color: string;
  connectionId: string;
  hoverdTableId: string | null;
  isConnecting: boolean;
  isFlatFile?: boolean;
  isWebApi?: boolean;
}

function middleEllipsis(text: string, max: number) {
  if (text.length <= max) return text;
  const half = Math.floor(max / 2);
  return text.slice(0, half) + '...' + text.slice(-half);
}
const Boolean = createSvgIcon(
  <svg
    viewBox="0 0 76 76"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    version="1.1"
    baseProfile="full"
    enableBackground="new 0 0 76.00 76.00"
    xmlSpace="preserve"
    fill="#000000"
  >
    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" stroke-linejoin="round"></g>
    <g id="SVGRepo_iconCarrier">
      {' '}
      <path
        fill="#000000"
        fill-opacity="1"
        stroke-linejoin="round"
        d="M 36,23L 30,23L 30,40L 25,40L 25,23L 19,23L 19,19L 36,19L 36,23 Z M 57,40L 50,40L 50,45L 56,45L 56,49L 50,49L 50,57L 45,57L 45,36L 57,36L 57,40 Z M 44,19L 48.5,19L 32.5,57L 28,57L 44,19 Z "
      ></path>{' '}
    </g>
  </svg>,
  'Boolean',
);
function CanvasColumn({
  fromCustomQueryTable,
  schema,
  database,
  column,
  tableId,
  idx,
  tablename,
  connectionId,
  hoverdTableId,
  isConnecting,
  isWebApi = false,
  isFlatFile = false,
}: ICanvasColumn) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [alert, setAlert] = useState<TAlert | null>(null);

  const isHovered = tableId === hoverdTableId;
  
  const itemTypeIcon = (type: string) => {
    type = type.toLowerCase();
    switch (type) {
      case 'integer':
        return (
          <img src="/Integer_Icon.svg" alt="Integer" style={{ height: '15px', width: '15px' }} />
        );

      case 'text':
        return <img src="/Text_Icon.svg" alt="Text" style={{ height: '15px', width: '15px' }} />;

      case 'timestamp':
        return <AccessTime style={{ height: '15px', width: '15px' }} />;

      case 'date':
        return (
          <CalendarToday
            style={{ height: '12px', width: '12px', marginLeft: '1px', marginRight: '2px' }}
          />
        );

      case 'decimal':
        return (
          <img src="/Decimal_Icon.svg" alt="Decimal" style={{ height: '15px', width: '15px' }} />
        );
      case 'boolean':
        return <Boolean style={{ height: '15px', width: '15px' }} />;

      case 'img':
        return <PhotoIcon style={{ height: '15px', width: '15px', color: 'gray' }} />;
      default:
        return null;
    }
  };
  const comboId = `${tablename}_${column.columnName}`;

  return (
    <Box
      id={column.uid}
      sx={{
        position: 'relative',
      }}
    >
      <Handle
        className="handle"
        type="source"
        data-testId={`${comboId}_source_left`}
        position={Position.Left}
        id={`${column.uid}-source-left`}
        style={{
          left: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 11,
          height: 11,
          background: '#2bb9bb',
          border: 'solid 1.5px white',
          zIndex: isHovered ? 10 : -10,
          opacity: isHovered && !isConnecting ? 1 : 0,
          pointerEvents: isHovered && !isConnecting ? 'auto' : 'none',
        }}
      />
      <Handle
        className="handle"
        type="source"
        position={Position.Right}
        data-testId={`${comboId}_source_right`}
        id={`${column.uid}-source-right`}
        style={{
          right: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 11,
          height: 11,
          background: '#2bb9bb',
          border: 'solid 1.5px white',
          zIndex: isHovered ? 10 : -10,
          opacity: isHovered && !isConnecting ? 1 : 0,
          pointerEvents: isHovered && !isConnecting ? 'auto' : 'none',
        }}
      />
      <Handle
        className="handle"
        type="target"
        position={Position.Left}
        data-testId={`${comboId}_target_left`}
        id={`${column.uid}-target-left`}
        style={{
          left: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 11,
          height: 11,
          background: '#2bb9bb',
          border: 'solid 1.5px white',
          zIndex: isHovered ? 10 : -10,
          opacity: isHovered && isConnecting ? 1 : 0,
          pointerEvents: isHovered && isConnecting ? 'auto' : 'none',
        }}
      />
      <Handle
        className="handle"
        type="target"
        position={Position.Right}
        data-testId={`${comboId}_target_right`}
        id={`${column.uid}-target-right`}
        style={{
          right: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 11,
          height: 11,
          background: '#2bb9bb',
          border: 'solid 1.5px white',
          zIndex: isHovered ? 10 : -10,
          opacity: isHovered && isConnecting ? 1 : 0,
          pointerEvents: isHovered && isConnecting ? 'auto' : 'none',
        }}
      />
      <div
        className="columnBox"
        data-no-drag
        data-no-pan
        id={column.uid}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'move',
          position: 'relative',
          // border: 'solid 0.5px #abababff',
        }}
        ref={boxRef}
        draggable
        onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => e.stopPropagation()}
        onTouchStart={(e: React.TouchEvent<HTMLDivElement>) => e.stopPropagation()}
        onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        onDragStart={(e) => {
          e.dataTransfer.setData('connectItemId', column.uid);
          e.dataTransfer.setData('connectIndex', idx.toString());
          e.dataTransfer.setData('connectTableName', tablename);
          e.dataTransfer.setData('connectColumnName', column.columnName);
          e.dataTransfer.setData('connectItemType', column.dataType);
          e.dataTransfer.setData('connecttableUid', tableId);
          e.dataTransfer.setData('schema', schema);
          e.dataTransfer.setData('database', `${database}`);
          e.dataTransfer.setData('tableId', tableId);
          e.dataTransfer.setData('tableHasCustomquery', `${fromCustomQueryTable}`);
          e.dataTransfer.setData('dbconnectionId', connectionId);
          e.dataTransfer.setData('isWebApi', `${isWebApi}`);
          e.dataTransfer.setData('isFlatFile', `${isFlatFile}`);
        }}
        onDragEnd={(e: React.DragEvent<HTMLDivElement>) => {
          // prevent bubbling (safety)
          e.stopPropagation();
        }}
      >
        <div
          className="columnItem"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {itemTypeIcon(column.dataType)}
        </div>
        <div
          style={{
            padding: '0 5px',
            maxWidth: '100%',
            height: 'fit-content',
            fontSize: fontSize.extraSmall,
            cursor: fromCustomQueryTable ? 'default' : 'move',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
          data-lastthree={column.columnName.slice(-3)}
        >
          {middleEllipsis(column.columnName, 30)}
        </div>
      </div>
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert.message}
          openAlert={!!alert}
        />
      )}
    </Box>
  );
}
export default CanvasColumn;
